import { ApiEnvelope, DsEntry, apiClient, authHeaders } from 'service/apiClient';
import localStorageService from 'service/localStorageService';

export const DS_CHANGED_EVENT = 'argon-ds-changed';

export type StashedDrop = {
  /** Empty when share-target: user picks folder in Upload miniapp. */
  destPath: string;
  existingPaths: string[];
  entries: FileSystemEntry[];
  files: File[];
};

const stash = new Map<string, StashedDrop>();
let stashSeq = 0;

/** Snapshot DataTransfer before the drop handler yields (Chromium clears it). */
export function snapshotDataTransfer(dt: DataTransfer): {
  entries: FileSystemEntry[];
  files: File[];
} {
  const entries: FileSystemEntry[] = [];
  for (const item of Array.from(dt.items)) {
    const entry = item.webkitGetAsEntry?.();
    if (entry) {
      entries.push(entry);
    }
  }
  return { entries, files: Array.from(dt.files) };
}

export function stashUploadDrop(drop: StashedDrop): string {
  const id = `up_${Date.now()}_${++stashSeq}`;
  stash.set(id, drop);
  return id;
}

export function getUploadDrop(id: string): StashedDrop | null {
  return stash.get(id) || null;
}

export function takeUploadDrop(id: string): StashedDrop | null {
  const next = stash.get(id) || null;
  if (next) {
    stash.delete(id);
  }
  return next;
}

export function releaseUploadDrop(id: string) {
  stash.delete(id);
}

export function notifyDsChanged(path: string) {
  window.dispatchEvent(new CustomEvent(DS_CHANGED_EVENT, { detail: { path } }));
}

const fileUploaderApi = {
  dsLs(path: string) {
    return apiClient.post<ApiEnvelope<DsEntry[]>>('/ds/ls', { path }, authHeaders());
  },

  dsMkdir(path: string) {
    return apiClient.post<ApiEnvelope<DsEntry>>('/ds/mkdir', { path }, authHeaders());
  },

  uploadFile(
    path: string,
    data: Blob,
    opts?: { onProgress?: (loaded: number, total: number) => void; signal?: AbortSignal }
  ) {
    return apiClient.post<void>('/api/files/upload', data, {
      params: { path },
      headers: {
        Authorization: `Bearer ${localStorageService.getSessionToken().getFullString()}`,
        'Content-Type': 'application/octet-stream',
      },
      timeout: 0,
      signal: opts?.signal,
      transformRequest: [(body) => body],
      onUploadProgress: (ev) => {
        opts?.onProgress?.(ev.loaded, ev.total || data.size);
      },
    });
  },
};

export default fileUploaderApi;
